import SectionWrapper from '@components/common/SectionWrapper'
import LoadingSpinner from '@components/shared/Loading/Loading'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { FaPlay } from 'react-icons/fa'

type Post = {
  name: string
  url: string
  description: string
  type?: string
  imageUrl?: string
  content?: string
}

const CACHE_KEY = 'lastPost'
const CACHE_TTL_MS = 60 * 60 * 1000 // 1 hour

type CachedPost = {
  data: Post
  timestamp: number
}

const Description = ({ text }: { text: string }) => (
  <div className=" text-xs md:text-sm leading-relaxed tracking-wide">
    {text.length > 120 ? `${text.slice(0, 120)}...` : text}
  </div>
)

// Extract YouTube embed URL from content HTML and add playsinline for mobile
const extractYoutubeEmbedUrl = (content?: string): string | null => {
  if (!content) return null
  const match = content.match(/src="(https:\/\/www\.youtube\.com\/embed\/[^"]+)"/)
  if (!match) return null

  // Add playsinline=1 to force inline playback on mobile
  const url = new URL(match[1])
  url.searchParams.set('playsinline', '1')
  return url.toString()
}

const LastKurantimePost = () => {
  const [lastPost, setLastPost] = useState<Post | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchLastPost = async () => {
      let cachedPost: CachedPost | null = null

      try {
        const cached = localStorage.getItem(CACHE_KEY)
        cachedPost = cached ? (JSON.parse(cached) as CachedPost) : null

        if (cachedPost && Date.now() - cachedPost.timestamp < CACHE_TTL_MS) {
          setLastPost(cachedPost.data)
          return
        }

        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          if (cachedPost?.data) {
            setLastPost(cachedPost.data)
          }
          return
        }

        const data = await fetch('/api/kurantime').then((res) => res.json())
        localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }))
        setLastPost(data)
      } catch (error) {
        console.error('Failed to fetch last Kurantime post:', error)
        if (cachedPost?.data) {
          setLastPost(cachedPost.data)
        }
      } finally {
        setLoading(false)
      }
    }

    try {
      const cached = localStorage.getItem(CACHE_KEY)
      if (cached) {
        const { data, timestamp }: CachedPost = JSON.parse(cached)
        if (Date.now() - timestamp < CACHE_TTL_MS || !navigator.onLine) {
          setLastPost(data)
        }
      }
    } catch {
      // Cache read is best-effort.
    }

    void fetchLastPost()
  }, [])

  const isVideo = lastPost?.type === 'video'
  const youtubeEmbedUrl = extractYoutubeEmbedUrl(lastPost?.content)

  return (
    <SectionWrapper>
      <div className="mx-auto w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-black  text-base md:text-lg tracking-tight">
            Son Kuran Time İçeriği
          </h3>
          {isVideo && (
            <span className="inline-flex items-center px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest bg-danger/10 text-danger border border-danger/20 sacred-shadow">
              <FaPlay className="mr-1.5" size={8} /> Video
            </span>
          )}
        </div>

        {lastPost?.name ? (
          isVideo && youtubeEmbedUrl ? (
            <div className="mt-2 group">
              {/* Embedded YouTube Video */}
              <div className="relative w-full aspect-video rounded-xl overflow-hidden mb-4 border  shadow-lg group-hover:border-brand-secondary/30 transition-all duration-500">
                <iframe
                  src={youtubeEmbedUrl}
                  title={lastPost.name}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>

              {/* Video Title */}
              <span className="font-black text-sm md:text-base  tracking-tight group-hover:text-brand-secondary transition-colors duration-300">
                {lastPost.name}
              </span>
            </div>
          ) : (
            // Standard Layout (Text Only or Non-Video)
            <div className="mt-2">
              <span className="font-black text-sm md:text-base  tracking-tight">
                {lastPost.name}
              </span>
              <div className="my-3 border-t  opacity-50" />
              <Description text={lastPost.description} />
              <Link
                className="block bg-brand-secondary hover:opacity-90 umi-event--last-kurantime-post-click mt-5 px-6 py-2.5 rounded-xl font-black text-white text-xs md:text-sm text-center transition-all duration-300 active:scale-[0.98] shadow-lg shadow-brand-secondary/20"
                href={lastPost.url}
                target="_blank"
              >
                Devamını oku
              </Link>
            </div>
          )
        ) : loading ? (
          <div className="flex-center min-h-[100px]">
            <LoadingSpinner fullScreen={false} />
          </div>
        ) : (
          <p className="py-3 text-sm text-muted">
            Son içerik internet bağlantısı olduğunda güncellenecek.
          </p>
        )}
      </div>
    </SectionWrapper>
  )
}

export default LastKurantimePost
